import type { OverlayProps } from 'react-bootstrap'
import { ButtonGroup, Stack } from 'react-bootstrap'
import type { FunctionComponent } from 'react'
import React, { useMemo, useRef } from 'react'
import { useTableContext } from '../Context'
import { TextTableFilter } from './TextTableFilter'
import { useTableFilterContext } from './Context'
import { HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import { BusyButton } from '@ztechapps/webui.button'
import type { ListField } from '@ztechapps/common.hook'
import { ListFieldTypeEnum } from '@ztechapps/common.hook'
import type { TableFilterProps } from './types'
import { ListTableFilter } from './ListTableFilter'
import { DateTableFilter } from './DateTableFilter'
import { NumberTableFilter } from './NumberTableFilter'
import { BooleanTableFilter } from './BooleanTableFilter'
import { PositiveCountTableFilter } from './PositiveCountTableFilter'
import { BasePopover } from '../BasePopover'

function tableFilterFactory(field: ListField): FunctionComponent<TableFilterProps> {
  const fieldType = field.type

  if (fieldType === ListFieldTypeEnum.select) {
    return ListTableFilter
  }

  if (fieldType === ListFieldTypeEnum.dateTimeZone || fieldType === ListFieldTypeEnum.date) {
    return DateTableFilter
  }

  if (fieldType === ListFieldTypeEnum.number) {
    return NumberTableFilter
  }

  if (fieldType === ListFieldTypeEnum.boolean) {
    return BooleanTableFilter
  }

  if (fieldType === ListFieldTypeEnum.positiveCountSelect) {
    return PositiveCountTableFilter
  }

  //fallback to return as text filter
  return TextTableFilter
}

export type FilterContainerProps = {
  target: OverlayProps['target']
}
export const FilterContainer = ({ target }: FilterContainerProps) => {
  const { name } = useTableContext()
  const { setShowFilterContainer, field, showFilterContainer } = useTableFilterContext()

  const TableFilter = useMemo(() => {
    return tableFilterFactory(field)
  }, [field])

  const focusRef = useRef<HTMLElement>(null)

  return (
    <BasePopover
      show={showFilterContainer}
      setShow={setShowFilterContainer}
      target={target}
      placement="bottom"
      name={name}
      focusRef={focusRef}
    >
      <Stack gap={3}>
        <div>
          <TableFilter field={field} focusRef={focusRef} />
        </div>
      </Stack>
    </BasePopover>
  )
}

export const FilterContainerBodyLayout = ({
  children,
  onOk,
}: {
  children: React.ReactNode
  onOk?: BusyButtonProps['onClick']
}) => {
  const { field, onFilter, setShowFilterContainer } = useTableFilterContext()

  // Individual field reset: Only clears the filter for this specific column.
  // This intentionally does NOT reset sorting or pagination - only global reset does that.
  const onClearFilter = () => {
    const tableFilter = { [field.name]: undefined }
    onFilter(tableFilter)
    setShowFilterContainer(false)
  }

  const _onOk = (e) => {
    onOk(e)
    setShowFilterContainer(false)
  }
  const onCancel = () => setShowFilterContainer(false)

  return (
    <Stack gap={3}>
      <div>{children}</div>
      <HorizontalStack className="justify-content-between border-top pt-3">
        <div>
          <BusyButton
            text="Reset"
            title={`Reset the filter for "${field.displayName}" column.`}
            bsStyle="warning"
            onClick={onClearFilter}
          />
        </div>

        <div>
          <ShowIf condition={!!onOk}>
            <ButtonGroup>
              <BusyButton
                variant="primary"
                text="Filter"
                title={`Apply the filter for "${field.displayName}" column.`}
                onClick={_onOk}
              />

              <BusyButton text="Cancel" onClick={onCancel} bsStyle="secondary" />
            </ButtonGroup>
          </ShowIf>
        </div>
      </HorizontalStack>
    </Stack>
  )
}
